import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RiseLoader } from 'react-spinners';
import LoadingCircle from '../LoadingCircle/LoadingCircle';
import ChatBubble from './components/ChatBubble';

/**
 * Props for the ChatBox component.
 */
interface ChatBoxProps {
  /**
   * Function to handle conversation with AI.
   * @param _ The message to send to the ai
   * @returns The ai's response
   */
  converse: (_: string) => Promise<string>;
  /**
   * (Optional) initial message from AI.
   */
  initialMessage?: string;
  /**
   * Indicates if the ChatBox is in a loading state.
   */
  isLoading: boolean;
}

/**
 * Enum representing the direction of messages.
 */
export enum MessageDirection {
  toAi,
  fromAi,
}

/**
 * Represents a single message in the chat history.
 */
interface MessageHistory {
  /**
   * The content of the message.
   */
  message: string;
  /**
   * Direction of the message (to AI or from AI).
   */
  toFrom: MessageDirection;
}

/**
 * ChatBox component manages the chat interface between the user and the AI.
 */
const ChatBox = (props: ChatBoxProps) => {
  const messagesEndRef = useRef<HTMLElement>(null);
  const theme = useTheme();
  const [message, setMessage] = useState<string>('');
  const [messageHistory, setMessageHistory] = useState<MessageHistory[]>([]);
  const [waitingForReply, setWaitingForReply] = useState(false);
  const [sendOrReplyError, setSendOrReplyError] = useState(false);

  /**
   * Initializes the message history with the initial message if provided.
   */
  useEffect(() => {
    setMessageHistory([]);
    if (props.initialMessage != undefined) {
      setMessageHistory((h) => [
        ...h,
        { message: props.initialMessage!, toFrom: MessageDirection.fromAi },
      ]);
    }
  }, [props.initialMessage]);

  /**
   * Scrolls to the bottom of the chat whenever the message history or loading states change.
   */
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messageHistory, waitingForReply, sendOrReplyError]);

  /**
   * Handles sending a message to the AI and updating the chat history with the response.
   */
  const sendMessage = useCallback(async () => {
    // Setup
    setWaitingForReply(true);
    setSendOrReplyError(false);
    setMessage('');

    // Record the sent message
    setMessageHistory((h) => [
      ...h,
      { message: message, toFrom: MessageDirection.toAi },
    ]);

    // Send the message
    try {
      const response = await props.converse(message);

      if (response == '') {
        setSendOrReplyError(true);
        return;
      }

      // Set the response
      setMessageHistory((h) => [
        ...h,
        { message: response, toFrom: MessageDirection.fromAi },
      ]);
    } catch (error) {
      setSendOrReplyError(true);
    } finally {
      setWaitingForReply(false);
    }
  }, [message, props]);

  return (
    <Card
      elevation={5}
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        minHeight: '20em',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <CardHeader
        title={<Typography variant="h6">Chat with S3G AI</Typography>}
        avatar={<SupportAgentRoundedIcon />}
      />
      <CardContent>
        <Box
          maxHeight="300px"
          overflow="auto"
          padding=".5em"
          display="flex"
          flexDirection="column"
          gap=".5em"
          ref={messagesEndRef}
        >
          {messageHistory.map((h, i) => (
            <ChatBubble key={i} message={h.message} toFrom={h.toFrom} />
          ))}
          <RiseLoader
            loading={waitingForReply}
            color={theme.palette.text.primary}
            size={8}
            style={{
              marginTop: 1,
            }}
          />
          {sendOrReplyError && (
            <Typography variant="caption" color="error" textAlign="center">
              An error occurred while sending a message. Please try again.
            </Typography>
          )}
        </Box>
        <Box marginTop={sendOrReplyError ? 0 : '.5em'} display="flex" gap="1em">
          <TextField
            sx={{ width: '100%' }}
            multiline
            value={message}
            onChange={(v) => setMessage(v.currentTarget.value)}
            disabled={waitingForReply}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault(); // Prevent newline insertion
                sendMessage();
              }
            }}
          />
          <Box display="flex" alignItems="center">
            <Tooltip title="Send Message" arrow>
              <IconButton onClick={sendMessage} disabled={waitingForReply}>
                <SendRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
      <LoadingCircle isLoading={props.isLoading} />
    </Card>
  );
};

export default ChatBox;
